// <footer-newsletter>: the footer's newsletter field subscribes through Klaviyo's client API, the
// same request the "Don't see your country?" flow makes (shipping-request.js). A signup that went
// through Shopify's customer form would reach Klaviyo with $source -50, which the welcome flow
// excludes; this source puts footer subscribers into the welcome series like popup subscribers.
//
// The element wraps Shopify's customer form, which stays the path without JavaScript or without a
// Klaviyo key. When the Klaviyo call fails (blocked by Safari Private Browsing or an ad blocker,
// offline, a timeout), the error is shown and the same form is posted to Shopify at once, so the
// address is never lost: the page reloads with Shopify's own thank-you, or its challenge page.
//
// Shopify's bot protection listens to every customer form on the page and, once hCaptcha passes,
// posts the form itself whether or not the submit was already handled. The form is marked
// data-nocaptcha while this element owns it, so a footer signup is never also posted to Shopify;
// the fallback post goes out like the form's no-JavaScript post, which Shopify answers with its own
// challenge page when it wants one.
if (!customElements.get("footer-newsletter")) {
  const ENDPOINT = "https://a.klaviyo.com/client/subscriptions/";
  const REVISION = "2026-07-15";
  const TIMEOUT = 15000;

  const subscriptionBody = (email, el) => ({
    data: {
      type: "subscription",
      attributes: {
        custom_source: el.dataset.source,
        profile: {
          data: {
            type: "profile",
            attributes: {
              email,
              properties: {
                umi_language: el.dataset.locale,
                umi_signup_source: "footer",
              },
              subscriptions: {
                email: { marketing: { consent: "SUBSCRIBED" } },
              },
            },
          },
        },
      },
      relationships: {
        list: { data: { type: "list", id: el.dataset.listId } },
      },
    },
  });

  class FooterNewsletter extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector("form");
      this.field = this.querySelector("[data-newsletter-field]");
      this.email = this.querySelector('input[type="email"]');
      this.status = this.querySelector("[data-newsletter-status]");
      this.serverError = this.querySelector("[data-newsletter-server-error]");
      // After Shopify's success post-back the field is gone and there is nothing to intercept.
      if (!this.form || !this.email || !this.status || !this.dataset.companyId)
        return;
      // After Shopify's error post-back the input points at that message; keep the link.
      this.describedBy = this.email.getAttribute("aria-describedby");
      this.form.dataset.nocaptcha = "true";
      this.form.addEventListener("submit", (event) => this.onSubmit(event));
      this.email.addEventListener("input", () => {
        if (!this.busy) this.showStatus("");
      });
      // Back to a page restored from the back/forward cache: the fallback post has gone, so the
      // form unlocks (and keeps posting to Shopify).
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) this.setBusy(false);
      });
    }

    async onSubmit(event) {
      // One submit at a time: the Klaviyo call, then (on failure) the Shopify post while the page
      // is leaving for it.
      if (this.busy) {
        event.preventDefault();
        return;
      }
      // hCaptcha binds on the first focus; if that happened before this script ran, Shopify's
      // protection already owns the form and will post it, so it keeps the submit.
      if (this.useShopifyForm || this.form.dataset.hcaptchaBound) return;
      event.preventDefault();
      this.setBusy(true);
      this.showStatus("");
      const ok = await this.post(this.email.value.trim());
      if (!this.isConnected) return;
      if (ok) {
        this.setBusy(false);
        this.field.hidden = true;
        this.showStatus(this.dataset.labelSuccess, true);
        this.status.focus();
      } else {
        this.useShopifyForm = true;
        this.showStatus(this.dataset.labelError);
        // submit() fires no submit event, so neither this handler nor Shopify's captcha script
        // intercepts it. The form stays locked until the page leaves.
        this.form.submit();
      }
    }

    async post(email) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT);
      try {
        const response = await fetch(
          `${ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/vnd.api+json",
              Accept: "application/vnd.api+json",
              revision: REVISION,
            },
            body: JSON.stringify(subscriptionBody(email, this)),
            signal: controller.signal,
          },
        );
        if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
        return true;
      } catch (e) {
        console.error("footer-newsletter: subscription not accepted", e);
        return false;
      } finally {
        clearTimeout(timer);
      }
    }

    // While a request is out the email cannot change, so what is sent is what is shown. The
    // button stays enabled (a disabled button would drop keyboard focus); onSubmit ignores it,
    // and the section's CSS dims it.
    setBusy(busy) {
      this.busy = busy;
      this.form.toggleAttribute("aria-busy", busy);
      this.email.readOnly = busy;
    }

    // The status region stays in the page and only its text changes, so screen readers announce it.
    // Once this element reports anything itself, Shopify's message from the last post-back is out of
    // date, so it goes, with the input's link to it.
    showStatus(message, success = false) {
      if (message && this.serverError) {
        this.serverError.remove();
        this.serverError = null;
        this.describedBy = null;
        this.email.removeAttribute("aria-invalid");
      }
      this.status.textContent = message;
      this.status.classList.toggle("site-footer__message--success", success);
      const describedBy =
        message && !success ? this.status.id : this.describedBy;
      if (describedBy) this.email.setAttribute("aria-describedby", describedBy);
      else this.email.removeAttribute("aria-describedby");
    }
  }
  customElements.define("footer-newsletter", FooterNewsletter);
}
